import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import "core-js/modules/es6.function.name.js";
import "core-js/modules/es6.string.link.js";
import "core-js/modules/es6.array.map.js";
import React from "react";

/**
 * NOTE: some of these utils are ports from article-utils in article-client. If
 * more utils are added, we should consider genericizing and extracting these
 * into a standalone module that multiple projects can use.
 */
import { unescape } from 'html-escaper';
import { PRODUCT_TYPES } from '@nerdwallet/marketplace/constants';
import { POST_TYPES } from '@nerdwallet/wp-api/constants';
import { getAuthors, getId, getPageTaxonomyDataElem, getPostTypeLabel, getSynopsis, getTitle, getType, getReviewers } from '@nerdwallet/wp-api/selectors/common';
import { getDateCalculatedIso } from '@nerdwallet/wp-api/utils/date';
import StructuredContent from '@nerdwallet/structured-content';
/*
 * Takes a WordPress API object and filters a subset of keys
 * to transform for what the ContentPageHeader API expects.
 *
 * @param  {Object}        author object with the shape of
 *                         {
 *                           name: {string} name of the author,
 *                           link: {string} URL pointing to the author's bio
 *                         }
 *
 * @return {Array}         author objects with the shape of the
 *                         ContentPageHeader `authors` prop.
 */

var mapAuthor = function mapAuthor(_ref) {
  var name = _ref.name,
      link = _ref.link;
  return {
    name: name,
    url: link
  };
};

var mapReviewer = function mapReviewer(reviewer) {
  var _reviewer$nw_data;

  return {
    name: reviewer === null || reviewer === void 0 ? void 0 : (_reviewer$nw_data = reviewer.nw_data) === null || _reviewer$nw_data === void 0 ? void 0 : _reviewer$nw_data.name,
    url: reviewer === null || reviewer === void 0 ? void 0 : reviewer.link
  };
};
/**
 * Takes the WordPress string content and unsanitizes it
 * to get the correct text.
 *
 * @param {String} wpContent html escaped wordpress content
 *                        '&amp;'
 *                        '&lt;', etc.
 *
 * @return {String}     content that is unescaped.
 *
 * @todo remove this method and it's usage once wordpress returns structured content
 *
 */


var unsanitize = function unsanitize(wpContent) {
  if (!wpContent) {
    return null;
  }

  return unescape(wpContent);
};
/**
 * Get the advertiser disclosure type for a given WP Post.
 *
 * @param {Object} entity WP Post.
 *
 * @return {string} Advertiser disclosure type.
 */


var getDisclosureType = function getDisclosureType(entity) {
  return (getPageTaxonomyDataElem(entity, 'vertical') || {}).value === 'Investing' ? PRODUCT_TYPES.INVESTING : PRODUCT_TYPES.CREDIT_CARDS;
};
/**
 * Get the editorial standards type for a given WP Post. Defaults to null.
 *
 * @param {Object} entity WP Post.
 *
 * @return {string} Editorial Standards type.
 */


var getEditorialStandardsType = function getEditorialStandardsType(entity) {
  return (getPageTaxonomyDataElem(entity, 'vertical') || {}).value === 'Investing' ? PRODUCT_TYPES.INVESTING : null;
};

var mapWordPress = function mapWordPress(entity) {
  var mappedProps;

  switch (getType(entity)) {
    // roundups render synopsis outside of the header
    case POST_TYPES.ROUNDUP:
      mappedProps = {
        synopsis: null
      };
      break;

    default:
      mappedProps = {};
  }

  var synopsis = getSynopsis(entity);

  if (synopsis) {
    synopsis = typeof synopsis === 'string' ? unsanitize(synopsis) : /*#__PURE__*/React.createElement(StructuredContent, null, synopsis);
  }

  return _objectSpread({
    advertiserDisclosureType: getDisclosureType(entity),
    authoringDate: getDateCalculatedIso(entity),
    authors: getAuthors(entity).map(mapAuthor),
    reviewers: getReviewers(entity).map(mapReviewer),
    eyebrowText: getPostTypeLabel(entity),
    editorialStandardsType: getEditorialStandardsType(entity),
    id: getId(entity),
    synopsis: synopsis,
    title: unsanitize(getTitle(entity))
  }, mappedProps);
};

var mapGraphQL = function mapGraphQL(entity) {
  var _entity$authors;

  return {
    advertiserDisclosureType: entity === null || entity === void 0 ? void 0 : entity.advertiserDisclosureType,
    authoringDate: (entity === null || entity === void 0 ? void 0 : entity.formattedDisplayDate) || (entity === null || entity === void 0 ? void 0 : entity.dateTimeDisplayDate),
    authors: (entity === null || entity === void 0 ? void 0 : (_entity$authors = entity.authors) === null || _entity$authors === void 0 ? void 0 : _entity$authors.map(function (_ref2) {
      var name = _ref2.name,
          link = _ref2.link;
      return {
        name: name,
        url: link
      };
    })) || [],
    reviewers: entity === null || entity === void 0 ? void 0 : entity.reviewedBy,
    eyebrowText: entity === null || entity === void 0 ? void 0 : entity.eyebrowText,
    editorialStandardsType: entity === null || entity === void 0 ? void 0 : entity.editorialDisclaimerType,
    id: entity === null || entity === void 0 ? void 0 : entity.id,
    synopsis: (entity === null || entity === void 0 ? void 0 : entity.stringSynopsis) || (entity !== null && entity !== void 0 && entity.structuredContentSynopsis ?
    /*#__PURE__*/
    // <ContentPageHeader> supports rendering arbitrary nodes as the synopsis,
    // so we'll render the Structured Content here to keep
    // `@nerdwallet/structured-content` out of the import path for folks using
    // just the vanilla CPH.
    React.createElement(StructuredContent, null, entity === null || entity === void 0 ? void 0 : entity.structuredContentSynopsis) : null),
    title: entity === null || entity === void 0 ? void 0 : entity.title,
    hideAdvertiserDisclosure: !(entity !== null && entity !== void 0 && entity.showAdvertiserDisclosure),
    hideAuthorByline: !(entity !== null && entity !== void 0 && entity.showAuthorByline),
    hideEditorialStandards: !(entity !== null && entity !== void 0 && entity.showEditorialDisclaimer),
    featuredImage: !(entity !== null && entity !== void 0 && entity.hideFeaturedImage) && (entity === null || entity === void 0 ? void 0 : entity.featuredImage)
  };
};

export default (function (props) {
  // eslint-disable-next-line no-underscore-dangle
  var isGraphQLObject = !!props.wordPressEntity.__typename;
  var mappedProps = isGraphQLObject ? mapGraphQL(props.wordPressEntity) : mapWordPress(props.wordPressEntity); // Merge props to allow manual overrides.

  return _objectSpread(_objectSpread({}, mappedProps), props);
});